import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import _get from 'lodash/get';
import _isFunction from 'lodash/isFunction';
import _identity from 'lodash/identity';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { displayName, isFragment } from 'helpers/core';
import 'components/Form/Input/Input.scss';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';
import './Input.scss';

// Only made a proper component so it has a displayName
const InputBox = props => <div {...props} />;
InputBox.displayName = 'InputBox';

const { block, element } = bem('hook-form-input');

const ControlledInput = React.forwardRef(({ value, onChange, format, parse, commonProps }, ref) => {
  const formatter = format || _identity;
  const parser = parse || _identity;
  const [state, setState] = useState(formatter(value));
  const handleChange = event => {
    const targetValue = event.target.value;
    setState(formatter(targetValue));
    onChange(parser(targetValue));
  };
  return <input {...commonProps} value={state} onChange={handleChange} ref={ref} />;
});

ControlledInput.displayName = 'ControlledInput';

ControlledInput.propTypes = {
  format: PropTypes.func,
  parse: PropTypes.func,
  value: PropTypes.string,
  onChange: PropTypes.func,
  commonProps: PropTypes.shape({
    className: PropTypes.string,
    placeholder: PropTypes.string,
    type: PropTypes.string,
  }),
};

const Input = props => {
  const {
    autoFocus,
    children,
    className,
    defaultValue,
    disabled,
    format,
    label,
    labelTooltip,
    name,
    noMargin,
    optional,
    parse,
    placeholder,
    placeholderValues,
    required,
    showRequiredLabel,
    type,
    ...restInputProps
  } = props;

  const intl = useIntl();
  const {
    control,
    register,
    formState: { errors },
    setFocus,
  } = useFormContext();

  useEffect(() => {
    if (autoFocus) {
      setFocus(name);
    }
  }, [setFocus]);

  const validators = makeValidators(props, intl);
  const hasError = Object.keys(errors).includes(name);
  const isHidden = type === 'hidden';
  let formattedPlaceholder = '';
  if (placeholder) {
    if (placeholder.defaultMessage && placeholderValues) {
      formattedPlaceholder = intl.formatMessage(placeholder, placeholderValues);
    } else {
      formattedPlaceholder = intl.formatMessage(placeholder);
    }
  }

  const commonProps = {
    className: element('input'),
    disabled,
    placeholder: formattedPlaceholder,
    type: isHidden ? 'text' : type,
    ...restInputProps,
  };

  let inputElement;
  if (!format && !parse) {
    inputElement = <input {...commonProps} defaultValue={defaultValue} {...register(name, { ...validators })} />;
  } else {
    // this replicates redux-form's format/parse props
    inputElement = (
      <Controller
        control={control}
        name={name}
        rules={validators}
        render={({ field }) => <ControlledInput {...field} commonProps={commonProps} format={format} parse={parse} />}
      />
    );
  }

  let renderPropChildren;
  if (!_isFunction(children)) {
    renderPropChildren = [<InputBox key="input-box">{inputElement}</InputBox>];
  } else {
    [renderPropChildren] = React.Children.toArray(
      children({
        InputBox,
        inputElement,
      })
    );
    renderPropChildren = isFragment(renderPropChildren)
      ? React.Children.toArray(renderPropChildren.props.children)
      : [renderPropChildren];
  }
  renderPropChildren = renderPropChildren.map(child =>
    displayName(child) !== 'InputBox'
      ? child
      : React.cloneElement(child, {
          className: element('input-container', { hasError }, _get(child, 'props.className')),
        })
  );

  return (
    <div className={block({ isHidden, noMargin }, className)}>
      <Label
        name={name}
        message={label}
        optional={optional}
        required={required}
        showRequiredLabel={showRequiredLabel}
        tooltip={labelTooltip}
      />
      {renderPropChildren}
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
};

Input.propTypes = {
  autoFocus: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  format: PropTypes.func,
  label: intlMessage,
  labelTooltip: intlMessage,
  name: PropTypes.string.isRequired,
  noMargin: PropTypes.bool,
  optional: PropTypes.bool,
  parse: PropTypes.func,
  placeholder: intlMessage,
  placeholderValues: PropTypes.shape({}),
  required: PropTypes.bool,
  showRequiredLabel: PropTypes.bool,
  type: PropTypes.string,
};

export default Input;
